import React, { useState } from 'react';
import {
  Autocomplete,
  TextField,
  Box,
  Typography,
  Chip
} from '@mui/material';
import { useTranslation } from '../../hooks/useTranslation';
import CustomDataDialog from './CustomDataDialog';

function CustomAutocomplete({
  options = [],
  value,
  onChange,
  label,
  dataType,
  placeholder,
  freeSolo = true,
  sx = {},
  ...props
}) {
  const { t } = useTranslation();
  const [showCustomDialog, setShowCustomDialog] = useState(false);
  const [customValue, setCustomValue] = useState('');

  const handleInputChange = (event, newInputValue) => {
    // Store the input value for potential custom data creation
    if (newInputValue && !options.includes(newInputValue)) {
      setCustomValue(newInputValue);
    }
  };

  const handleBlur = (event) => {
    // If user tabs out or clicks away with a custom value, show the dialog
    const inputValue = event.target.value;
    if (inputValue && !options.includes(inputValue) && inputValue !== value) {
      setCustomValue(inputValue);
      setShowCustomDialog(true);
    }
  };

  const handleChange = (event, newValue) => {
    // If the user selected a value that's not in the options, show the custom dialog
    if (newValue && !options.includes(newValue)) {
      setCustomValue(newValue);
      setShowCustomDialog(true);
      return;
    }
    
    // Otherwise, just update the value normally
    onChange(newValue);
  };

  const handleCustomDataConfirm = (newValue) => {
    // Update the options list with the new custom value
    const updatedOptions = [...options, newValue].sort();
    
    // Call the onChange with the new value
    onChange(newValue);
    
    // Update the parent component's options if it has a way to do so
    if (props.onOptionsUpdate) {
      props.onOptionsUpdate(updatedOptions);
    }
  };

  const handleCustomDataClose = () => {
    setShowCustomDialog(false);
    setCustomValue('');
    // Reset the value to empty if user cancels
    onChange('');
  };

  const getDataTypeLabel = () => {
    const labels = {
      brands: t('common.brand') || 'Brand',
      series: t('common.series') || 'Series',
      types: t('common.type') || 'Type',
      scales: t('common.scale') || 'Scale',
      sources: t('common.source') || 'Source'
    };
    return labels[dataType] || dataType;
  };

  return (
    <Box sx={sx}>
      <Autocomplete
        freeSolo={freeSolo}
        options={options}
        value={value}
        onChange={handleChange}
        onInputChange={handleInputChange}
        renderInput={(params) => (
          <TextField
            {...params}
            label={label}
            placeholder={placeholder}
            fullWidth
            onBlur={handleBlur}
          />
        )}
        renderOption={(props, option) => (
          <Box component="li" {...props}>
            <Typography variant="body2">{option}</Typography>
            {options.includes(option) && (
              <Chip
                label={t('customData.standard') || 'Standard'}
                size="small"
                sx={{ ml: 1, height: 20, fontSize: '0.7rem' }}
                color="primary"
                variant="outlined"
              />
            )}
          </Box>
        )}
        renderTags={(value, getTagProps) =>
          value.map((option, index) => (
            <Chip
              {...getTagProps({ index })}
              key={option}
              label={option}
              size="small"
            />
          ))
        }
        {...props}
      />
      


      <CustomDataDialog
        open={showCustomDialog}
        onClose={handleCustomDataClose}
        onConfirm={handleCustomDataConfirm}
        dataType={dataType}
        initialValue={customValue}
        title={t('customData.addNew', { type: getDataTypeLabel() }) || `Add New ${getDataTypeLabel()}`}
      />
    </Box>
  );
}

export default CustomAutocomplete;
